import { Injectable } from '@angular/core';
import { CanActivate } from '@angular/router';
import { AuthService } from '../auth/auth.service'
import { ProfileService } from '../profile/profile.service'
import { ClientConfigService } from '../clientconfig/clientconfig.service'
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot } from '@angular/router';
import { BotService } from '../../chatstyle/bot/bot.service';
import { Observable, of } from 'rxjs';
import { switchMap, map, catchError } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class AuthGuardService implements CanActivate {

  constructor(private authService: AuthService,
    private profileService: ProfileService,
    private router: Router,
    private botService: BotService) {
}

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
    console.log(state.url);
    
    // Wait for user to be loaded before checking login status
    return this.authService.isLoggedIn$().pipe(
      switchMap(isLoggedIn => {
        if (isLoggedIn) {
          return this.handleLoggedInUser(route, state);
        }

        this.authService.startAuthentication(state);
        return of(false);
      })
    );
  }

  private handleLoggedInUser(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
    const failurl = ClientConfigService.settings.post_logout_redirect_uri;
    console.log(failurl);

    if ("claims" in route.data) {
      const claims = route.data.claims as Array<string>;
      const userClaims = this.authService.getClaims();

      for (let index = 0; index < claims.length; ++index) {
        if (claims[index] in userClaims) {
          if ("value" in route.data) {
            if (userClaims[claims[index]] !== route.data.value) {
              this.router.navigateByUrl(failurl);
              return of(false);
            }
          }
          return of(true);
        }
      }

      this.router.navigateByUrl(failurl);
      return of(false);
    }

    if ("privilege" in route.data) {
      return this.profileService.load().pipe(
        switchMap(pro => {
          const priv = route.data.privilege;

          // Check if user is in onboarding state
          const isOnboarding = this.profileService.isOnboarding();
          const isSetupUrl = state.url.includes('/setup/');
          const isSuperAdmin = this.profileService.hasPrivilege('SuperAdmin');
          console.log('AuthGuard - Onboarding Check:', {
            isOnboarding,
            isSetupUrl,
            isSuperAdmin,
            url: state.url,
            privileges: this.profileService.profile?.privileges,
            profileData: this.profileService.profile
          });

          if (isOnboarding && !isSetupUrl) {
            // New users should be redirected to the setup wizard
            console.log('AuthGuard - Redirecting to setup wizard because user has Onboarding privilege');
            this.router.navigateByUrl('/setup/setupwizard/step1');
            return of(false);
          }

          // Check bot state for non-SuperAdmin users (only after onboarding is complete and not accessing setup)
          if (!isOnboarding && !isSuperAdmin && !isSetupUrl) {
            return this.botService.getBots().pipe(
              map(bots => {
                if (!bots || bots.length === 0) {
                  // No bots = no org association (user was removed from org)
                  console.log('AuthGuard - No bots found, redirecting to setup wizard');
                  this.router.navigateByUrl('/setup/setupwizard/step1');
                  return false;
                }

                const hasSetupBot = bots.some(bot => bot.state === 'setup');
                const isOnboardingUser = this.profileService.hasPrivilege('Onboarding');

                if (hasSetupBot && isOnboardingUser) {
                  // Only redirect to onboarding if the user owns the setup
                  // (invited users have 'User' privilege, not 'Onboarding')
                  console.log('AuthGuard - Redirecting to setup wizard because bot is in setup state');
                  this.router.navigateByUrl('/setup/setupwizard/step1');
                  return false;
                }

                // Bot is ready, continue with privilege check
                return this.checkPrivilegesSync(priv);
              }),
              catchError(err => {
                console.error('AuthGuard - Error checking bot state:', err);
                // On error, allow access (fail open)
                return of(this.checkPrivilegesSync(priv));
              })
            );
          }

          // For SuperAdmin or onboarding users, check privileges directly
          return of(this.checkPrivilegesSync(priv));
        }),
        catchError(reason => {
          return of(false);
        })
      );
    }

    return of(true);
  }

  /**
   * Helper method to check if user has required privilege
   */
  private checkPrivilegesSync(priv: string): boolean {
    const failurl = ClientConfigService.settings.post_logout_redirect_uri;

    if (this.profileService.profile && this.profileService.profile.privileges) {
      const userPrivs = this.profileService.profile.privileges;

      if (userPrivs !== null && userPrivs.indexOf(priv) >= 0) {
        return true;
      }
    }

    this.router.navigateByUrl(failurl);
    return false;
  }
}
